import { Box, Inline, Stack, Text, Tiles } from '@front10/base-elements';
import parseISO from 'date-fns/parseISO';
import React, { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { DateEvent, DayData, MonthHandler } from '../types';
import { getMonthNumber } from './helper';
import { FnOnDayClick, FnOnDayHover } from './hooks';
import MonthItem, { MonthItemProps } from './MonthItem';
import { useTranslation } from '@front10/utils';

export type CalendarContentProps = Pick<
  MonthItemProps,
  'weekdayItem' | 'dayItems' | 'captionItem'
> & {
  formatMonth?: string;
  numberOfMonths?: number;
  renderDay?: any;
  onDayClick?: FnOnDayClick;
  onDayHover?: FnOnDayHover;
  displayDate: Date;
  initialDate: Date;
  months: Array<MonthHandler>;
  onScroll?: (e: any) => void;
  isMobile?: boolean;
  showYear?: boolean;
  showWeekDayNames?: boolean;
  weekDaysContainer?: any;
  calendarContainer?: any;
  calendarMonthContainer?: any;
  dispatch?: any;
  currentDate?: any;
  preSelectedDate?: Date;
  setWasOpenPopup?: (value: boolean) => void;
  events?: DateEvent[];
};

const CalendarContent = React.forwardRef<HTMLDivElement, CalendarContentProps>(
  (
    {
      captionItem,
      dayItems,
      weekdayItem,
      formatMonth = 'MMMM YYYY',
      numberOfMonths = 2,
      renderDay,
      onScroll = () => {},
      onDayClick = () => {},
      onDayHover = () => {},
      displayDate = new Date(),
      months,
      children,
      isMobile = false,
      showYear,
      showWeekDayNames = true,
      weekDaysContainer,
      calendarContainer,
      calendarMonthContainer,
      setWasOpenPopup,
      currentDate,
      dispatch,
      preSelectedDate,
      initialDate,
      events: groupedEvents
    }: PropsWithChildren<CalendarContentProps>,
    ref
  ) => {
    const monthRef = useRef<HTMLDivElement>(null);
    const translate = useTranslation();
    const events = useMemo(() => {
      return groupedEvents
        ?.map((event) =>
          event.dates.map((date) => ({
            date: parseISO(date),
            label: translate(event.label, event.label),
            styles: event.styles
          }))
        )
        .flat();
    }, [groupedEvents, translate]);
    useEffect(() => {
      if (isMobile) {
        setTimeout(() => {
          if (monthRef && monthRef.current) {
            monthRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }, 250);
      }
      if (!isMobile) {
        if (currentDate?.start) {
          dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: currentDate?.start });
        } else {
          dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: initialDate });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDate]);

    const handleDayClick = (dayData: DayData) => {
      onDayClick(dayData);
    };
    return (
      <Stack
        className={isMobile ? 'calendar-content-container-mobile' : 'calendar-content-container'}
        ref={ref}
        dataTestId="calendar-months-container"
        css={{ width: isMobile ? undefined : 'min-content' }}
        {...calendarContainer}
      >
        <Box>
          {months.map((month, index) => {
            const isSelectedMonth =
              currentDate !== null &&
              currentDate !== undefined &&
              month.getMonth().getMonth() === currentDate.start?.getMonth() &&
              month.getMonth().getFullYear() === currentDate.start?.getFullYear();

            return (
              <MonthItem
                ref={isSelectedMonth ? monthRef : undefined}
                captionItem={captionItem}
                weekdayItem={weekdayItem}
                dayItems={dayItems}
                key={`month-${getMonthNumber(displayDate) + index}-${index}`}
                month={month}
                renderDay={renderDay}
                onDayClick={handleDayClick}
                onDayHover={onDayHover}
                showYear={showYear}
                showWeekDayNames={showWeekDayNames}
                weekDaysContainer={weekDaysContainer}
                calendarMonthContainer={calendarMonthContainer}
                setWasOpenPopup={setWasOpenPopup}
                isMobile={isMobile}
                preSelectedDate={preSelectedDate}
                events={events}
              />
            );
          })}
          {children}
        </Box>
        {groupedEvents ? (
          <Tiles columns={2} colGap={3} rowGap={3} mx="3" mt="4" mb="2">
            {groupedEvents.map((event) => (
              <Inline key={event.label} alignY="baseline" gap={2}>
                <Box sx={event.styles} css={{ width: '14px', height: '10px' }} />
                <Text variant="fontSize7" sx={{ fontWeight: 'normal' }}>
                  {translate(event.label, event.label)}
                </Text>
              </Inline>
            ))}
          </Tiles>
        ) : null}
      </Stack>
    );
  }
);

export default CalendarContent;
